import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import NumberInput from "../../../framework/NumberInput";
import SelectInput from "../../../framework/SelectInput";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { saveHsn } from "../../../services/hsn-services";
import { gstValues } from "../../../utils/constant";
import { formStateHandler, isEmpty } from "../../../utils/utils";
import { transformPayload } from "../HsnConstants";
import classes from "./index.module.css";

const AddHsn = ({
  id,
  onSaveHsn = () => {},
  onEscape = () => {},
  hsnData = null,
}) => {
  // const {
  //   focusElement,
  // } = useContext(GlobalContext);
  const [formState, setFormState] = useState({});
  const hsnCodeRef = useRef("");
  const gstRef = useRef("");
  const hsnremarksref = useRef("");
  const hsnDescriptionRef = useRef("");
  const withHsnValueCap = ({ value }) => value.length <= 8;
  const saveHsnRef = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);

  const validateForm = useCallback(async (fieldName, data) => {
    if (fieldName === "hsnCode" || !fieldName) {
      if (
        isEmpty(hsnCodeRef.current.value) ||
        hsnCodeRef.current.value.length > 8 ||
        hsnCodeRef.current.value.length < 6
      ) {
        displayToast({
          type: "error",
          title: "Please enter a valid HSN Code",
        });
        hsnCodeRef.current?.focus();
        return false;
      }
    }
    if (fieldName === "gst" || !fieldName) {
      if (isEmpty(gstRef.current.value)) {
        displayToast({
          type: "error",
          title: "Please enter a valid GST",
        });
        gstRef.current?.focus();
        return false;
      }
    }

    return true;
  }, []);
  const updateHsnHandler = useCallback(
    async (e) => {
      if (!(await validateForm(null, formState))) return;
      const payload = transformPayload({ ...formState });
      return saveHsn(payload).then((r) => {
        if (!r) return;
        onSaveHsn(r);
        return displayToast({
          type: "success",
          title: "Hsn added successfully.",
        });
      });
    },
    [formState, onSaveHsn, validateForm]
  );
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveHsnRef.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      const updatedFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options,
        sendOption
      );
      setFormState(updatedFormState);
    },
    [formState]
  );

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    if (hsnData) {
      setFormState({
        ...hsnData,
        gst: gstValues.find((g) => g.id === hsnData.gstPercent),
      });
    }
  }, [hsnData]);
  return (
    <div>
      <h4 className={classes["hsn-detail-label"]}>HSN Details</h4>
      <Grid container spacing={2} sx={{ p: "25px 20px" }}>
        <Grid item xs={4}>
          <NumberInput
            id="add_hsn_code"
            name="hsnCode"
            label="HSN Code"
            placeholder="Enter HSN Code"
            ref={hsnCodeRef}
            value={formState?.hsnCode || ""}
            autoFocus
            onFocus={() => setCurrentComponent("add_hsn_code")}
            onChange={(e) => {
              handleFormChange("hsnCode", e.target.value);
            }}
            isAllowed={withHsnValueCap}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_hsn_code",
                down: "add_hsn_gst",
                left: "hsn_remarks",
                right: "hsn_description",
              }),
            }}
            allowNegative={false}
            required
          />
        </Grid>
        <Grid item xs={8}>
          <TextInput
            id="hsn_description"
            ref={hsnDescriptionRef}
            name="description"
            label="Description"
            value={formState?.description || ""}
            onFocus={() => setCurrentComponent("hsn_description")}
            onChange={(e) => {
              handleFormChange("description", e.target.value);
            }}
            onBlur={() => validateForm("description", formState)}
            maxInputLength={2000}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "hsn_description",
                down: "hsn_remarks",
                left: "add_hsn_code",
                right: "add_hsn_gst",
              }),
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectInput
            id="add_hsn_gst"
            ref={gstRef}
            name="hsnGst"
            label="GST"
            value={formState?.gst || null}
            onFocus={() => setCurrentComponent("add_hsn_gst")}
            onChange={(event, option) => {
              handleFormChange("gst", option);
            }}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => {
              return option.label;
            }}
            options={gstValues}
            markers={JSON.stringify({
              up: "add_hsn_code",
              down: "hsn_description",
              left: "hsn_description",
              right: "hsn_remarks",
            })}
            required
          />
        </Grid>
        <Grid item xs={8}>
          <TextInput
            id="hsn_remarks"
            ref={hsnremarksref}
            name="remarks"
            label="Remarks"
            value={formState?.remarks || ""}
            onFocus={() => setCurrentComponent("hsn_remarks")}
            onChange={(e) => {
              handleFormChange("remarks", e.target.value);
            }}
            maxInputLength={2000}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "hsn_description",
                down: "hsn_remarks",
                left: "add_hsn_gst",
                right: "add_hsn_code",
              }),
            }}
          />
        </Grid>
      </Grid>
      <div className={classes["add-hsn-save-button-wrapper"]}>
        <Button
          id="add_hsn_save_button"
          ref={saveHsnRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save HSN"
          color="violet"
          onClick={updateHsnHandler}
        />
      </div>
    </div>
  );
};

export default AddHsn;
